import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../services/firebase";
import HRCampusSelector from "../../components/HRCampusSelector";
import { getCurrentUser } from "../../auth";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function payrollLsKey(campus) {
  return `payroll_${String(campus || "gen").toLowerCase()}`;
}
function loadLocalPayroll(campus) {
  try {
    const raw = localStorage.getItem(payrollLsKey(campus));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export default function PayrollPrint() {
  const navigate = useNavigate();
  const q = useQuery();
  const currentUser = getCurrentUser();
  const roleStr = String(currentUser?.role || "").toLowerCase();
  const rolesArr = Array.isArray(currentUser?.roles) ? currentUser.roles.map(r => String(r).toLowerCase()) : [];
  const hasRole = (r) => roleStr === r || rolesArr.includes(r);
  const isExec = hasRole("admin") || hasRole("superadmin") || hasRole("vicepresident") || hasRole("vp") || hasRole("board") || hasRole("bot") || String(currentUser?.campus || "").toLowerCase() === "all";
  const canReadAllPayroll = isExec || hasRole("finance");
  const [selectedCampus, setSelectedCampus] = useState(q.get("campus") || "bulacan");
  const selectedPeriod = q.get("period") || "";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodLabel, setPeriodLabel] = useState("");
  const [compact, setCompact] = useState(false);

  function normStr(s) {
    return String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
  }
  function keyOf(rec) {
    return `${normStr(rec.employeeName)}|${String(rec.payPeriod || "")}|${normStr(rec.campus || selectedCampus)}`;
  }
  function dedup(list) {
    const map = {};
    for (const r of list) {
      const k = keyOf(r);
      const prev = map[k];
      if (!prev) map[k] = r;
      else {
        const prevLocal = String(prev.id || "").startsWith("LS-");
        const currLocal = String(r.id || "").startsWith("LS-");
        if (prevLocal && !currLocal) map[k] = r;
      }
    }
    return Object.values(map);
  }

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const payrollCollection = collection(db, "payroll");
        let ref = payrollCollection;
        const campusToUse = canReadAllPayroll ? selectedCampus : String(currentUser?.campus || selectedCampus || "").toLowerCase();
        if (campusToUse) {
          ref = query(ref, where("campus", "==", String(campusToUse).toLowerCase()));
        }
        if (selectedPeriod) {
          ref = query(ref, where("payPeriod", "==", selectedPeriod));
        }
        const payrollSnapshot = await getDocs(ref);
        const list = payrollSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setRows(dedup(list));
      } catch {
        const localCampus = canReadAllPayroll ? selectedCampus : String(currentUser?.campus || selectedCampus || "").toLowerCase();
        const local = loadLocalPayroll(localCampus).filter(r => (!selectedPeriod || (r.payPeriod || "") === selectedPeriod));
        setRows(dedup(local));
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [selectedCampus, selectedPeriod, canReadAllPayroll]);

  useEffect(() => {
    if (!selectedPeriod) {
      setPeriodLabel("Selected Period");
      return;
    }
    try {
      const d = new Date(selectedPeriod);
      const y = d.getFullYear();
      const m = d.toLocaleString(undefined, { month: "long" });
      const start = d.getDate();
      const end = start === 1 ? 15 : new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      setPeriodLabel(`${m} ${start}\u2013${end}, ${y}`);
    } catch {
      setPeriodLabel(selectedPeriod);
    }
  }, [selectedPeriod]);

  const totalAmount = useMemo(() => {
    return rows.reduce((sum, r) => sum + Number(r.amountToBePaid || 0), 0);
  }, [rows]);

  function simplePrint() {
    const compactCss = compact ? `
      table { font-size: 11px; }
      th, td { padding: 4px 6px; }
    ` : ``;
    const style = `
      <style>
        @page { size: legal landscape; margin: 0.5in; }
        body { font-family: Arial, sans-serif; color: #000; }
        h3, h4 { margin: 0 0 12px 0; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #000; padding: 6px 8px; text-align: left; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        tr { page-break-inside: avoid; }
        .sig { margin-top: 24px; display: flex; justify-content: space-between; gap: 40px; }
        .sig .col { width: 45%; }
        .line { border-top: 1px solid #000; height: 24px; margin: 24px 0 6px 0; }
        .name { font-weight: 600; }
        .title { font-size: 0.9rem; }
        ${compactCss}
      </style>
    `;
    const header = `<h3>Payroll Summary — ${periodLabel}</h3><div>Campus: ${String(selectedCampus || "").toUpperCase()}</div>`;
    const thead = `
      <thead>
        <tr>
          <th>Employee Name</th>
          <th>Basic Pay (Bi-Monthly)</th>
          <th>Overtime</th>
          <th>Substitution</th>
          <th>Absences/Undertime</th>
          <th>SSS</th>
          <th>PhilHealth</th>
          <th>PAG-IBIG</th>
          <th>BIR</th>
          <th>Books & Misc.</th>
          <th>Contri</th>
          <th>COOP</th>
          <th>Uniform</th>
          <th>Cash Advance</th>
          <th>Total Deduction</th>
          <th>Payable Before Deduction</th>
          <th>Amount to be Paid</th>
          <th>Employee Signature</th>
        </tr>
      </thead>
    `;
    const tbody = `
      <tbody>
        ${rows.map(r => `
          <tr>
            <td>${r.employeeName || ""}</td>
            <td>₱${Number(r.basicPay || 0).toLocaleString()}</td>
            <td>₱${Number(r.overtime || 0).toLocaleString()}</td>
            <td>₱${Number(r.substitution || 0).toLocaleString()}</td>
            <td>₱${Number(r.absences || 0).toLocaleString()}</td>
            <td>₱${Number((Number(String(r.payPeriod || "").split("-")[2] || 0) === 1) ? (r.sss || 0) : 0).toLocaleString()}</td>
            <td>₱${Number((Number(String(r.payPeriod || "").split("-")[2] || 0) === 1) ? (r.philhealth || 0) : 0).toLocaleString()}</td>
            <td>₱${Number((Number(String(r.payPeriod || "").split("-")[2] || 0) === 1) ? 0 : (r.pagIbig || 0)).toLocaleString()}</td>
            <td>₱${Number((Number(String(r.payPeriod || "").split("-")[2] || 0) === 1) ? 0 : (r.bir || 0)).toLocaleString()}</td>
            <td>₱${Number(r.booksMisc || 0).toLocaleString()}</td>
            <td>₱${Number(r.contri || 0).toLocaleString()}</td>
            <td>₱${Number(r.coop || 0).toLocaleString()}</td>
            <td>₱${Number(r.uniform || 0).toLocaleString()}</td>
            <td>₱${Number(r.cashAdvance || 0).toLocaleString()}</td>
            <td>₱${Number(r.totalDeduction || 0).toLocaleString()}</td>
            <td>₱${Number(r.payableBeforeDeduction || 0).toLocaleString()}</td>
            <td>₱${Number(r.amountToBePaid || 0).toLocaleString()}</td>
            <td><div class="line"></div></td>
          </tr>
        `).join("")}
      </tbody>
    `;
    const tfoot = `
      <tfoot>
        <tr>
          <td colspan="16" style="text-align:right;font-weight:600;">Total Amount to be Paid</td>
          <td style="font-weight:700;">₱${Number(totalAmount || 0).toLocaleString()}</td>
          <td></td>
        </tr>
      </tfoot>
    `;
    const sig = `
      <div class="sig">
        <div class="col">
          <div class="line"></div>
          <div class="name">Prepared by: Catalina T. Basa</div>
          <div class="title">Finance Officer</div>
        </div>
        <div class="col">
          <div class="line"></div>
          <div class="name">Approved by: Marie Lyra Lizardo Oaing</div>
          <div class="title">Vice President of the Board of Trustees</div>
        </div>
      </div>
    `;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />${style}</head><body>${header}<table>${thead}${tbody}${tfoot}</table>${sig}</body></html>`;
    try {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow && iframe.contentWindow.document ? iframe.contentWindow.document : null;
      if (!doc) {
        console.warn("Print iframe not available; falling back to window.print");
        window.print();
        return;
      }
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        try { iframe.contentWindow && iframe.contentWindow.focus(); } catch (e) { console.warn("focus failed"); }
        try { iframe.contentWindow && iframe.contentWindow.print(); } catch (e) { console.warn("iframe print failed"); }
        setTimeout(() => {
          try { document.body.removeChild(iframe); } catch (e) { console.warn("cleanup failed"); }
        }, 800);
      }, 400);
    } catch (err) {
      console.warn("Simple Print failed; using window.print");
      try { window.print(); } catch (e) { console.warn("window.print failed"); }
    }
  }

  return (
    <div id="print-root" className="payroll-print-page container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3 print-hide">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">Payroll Print Preview</h4>
          <HRCampusSelector selectedCampus={selectedCampus} onChange={setSelectedCampus} />
        </div>
        <div className="d-flex gap-2">
          <div className="form-check form-switch d-flex align-items-center me-3">
            <input className="form-check-input" type="checkbox" id="compactMode" checked={compact} onChange={e => setCompact(e.target.checked)} />
            <label className="form-check-label ms-2" htmlFor="compactMode">Compact mode</label>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Back</button>
          <button className="btn btn-primary" onClick={simplePrint}>Print</button>
          <button className="btn btn-outline-primary" onClick={() => window.print()}>Advanced Print</button>
        </div>
      </div>
      <div className="mb-3">
        <h5 className="mb-1">Payroll Summary — {periodLabel || "Selected Period"}</h5>
        <div className="text-muted">Campus: {selectedCampus.toUpperCase()}</div>
      </div>
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Employee Name</th>
              <th>Basic Pay (Bi-Monthly)</th>
              <th>Overtime</th>
              <th>Substitution</th>
              <th>Absences/Undertime</th>
              <th>SSS</th>
              <th>PhilHealth</th>
              <th>PAG-IBIG</th>
              <th>BIR</th>
              <th>Books & Misc.</th>
              <th>Contri</th>
              <th>COOP</th>
              <th>Uniform</th>
              <th>Cash Advance</th>
              <th>Total Deduction</th>
              <th>Payable Before Deduction</th>
              <th>Amount to be Paid</th>
              <th>Employee Signature</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="18" className="text-center py-4">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="18" className="text-center py-4">No payroll records found for the selected filter</td></tr>
            ) : rows.map(r => (
              <tr key={r.id}>
                <td>{r.employeeName}</td>
                <td>₱{Number(r.basicPay || 0).toLocaleString()}</td>
                <td>₱{Number(r.overtime || 0).toLocaleString()}</td>
                <td>₱{Number(r.substitution || 0).toLocaleString()}</td>
                <td>₱{Number(r.absences || 0).toLocaleString()}</td>
                <td>₱{Number((Number(String(r.payPeriod || "").split("-")[2] || 0) === 1) ? (r.sss || 0) : 0).toLocaleString()}</td>
                <td>₱{Number((Number(String(r.payPeriod || "").split("-")[2] || 0) === 1) ? (r.philhealth || 0) : 0).toLocaleString()}</td>
                <td>₱{Number((Number(String(r.payPeriod || "").split("-")[2] || 0) === 1) ? 0 : (r.pagIbig || 0)).toLocaleString()}</td>
                <td>₱{Number((Number(String(r.payPeriod || "").split("-")[2] || 0) === 1) ? 0 : (r.bir || 0)).toLocaleString()}</td>
                <td>₱{Number(r.booksMisc || 0).toLocaleString()}</td>
                <td>₱{Number(r.contri || 0).toLocaleString()}</td>
                <td>₱{Number(r.coop || 0).toLocaleString()}</td>
                <td>₱{Number(r.uniform || 0).toLocaleString()}</td>
                <td>₱{Number(r.cashAdvance || 0).toLocaleString()}</td>
                <td>₱{Number(r.totalDeduction || 0).toLocaleString()}</td>
                <td>₱{Number(r.payableBeforeDeduction || 0).toLocaleString()}</td>
                <td>₱{Number(r.amountToBePaid || 0).toLocaleString()}</td>
                <td><div className="signature-line" style={{ minWidth: 140 }} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="16" className="text-end fw-bold">Total Amount to be Paid</td>
              <td className="fw-bold">₱{Number(totalAmount || 0).toLocaleString()}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="signature-block mt-4">
        <div className="signature-col">
          <div className="signature-line"></div>
          <div className="signature-name">Prepared by: Catalina T. Basa</div>
          <div className="signature-title">Finance Officer</div>
        </div>
        <div className="signature-col">
          <div className="signature-line"></div>
          <div className="signature-name">Approved by: Marie Lyra Lizardo Oaing</div>
          <div className="signature-title">Vice President of the Board of Trustees</div>
        </div>
      </div>
    </div>
  );
}
